import { useCampaignStore } from '@/stores/campaignStore';
import { authenticatedFetch } from '@/utils/auth';
import { locationService } from '@/utils/locationService';
import NetInfo from '@react-native-community/netinfo';
import * as Sentry from '@sentry/react-native';
import { AppState } from 'react-native';

export interface SyncResult {
  success: boolean;
  syncedItems: number;
  errors: string[];
}

class SyncService {
  private isSyncing = false;
  private syncInterval: number | null = null;
  private appStateListener: any = null;
  private networkListener: any = null;

  // Start automatic sync
  startAutoSync(intervalMs: number = 30000) { // Default 30 seconds
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
    
    // Set up app state listener to handle background/foreground transitions
    if (!this.appStateListener) {
      this.appStateListener = AppState.addEventListener('change', (nextAppState) => {
        if (nextAppState === 'active') {
          // Trigger a sync check when app becomes active
          this.checkAndSyncIfNeeded();
        }
      });
    }

    // Set up network state listener to trigger sync when internet is restored
    if (!this.networkListener) {
      this.networkListener = NetInfo.addEventListener((state) => {
        if (state.isConnected && state.isInternetReachable) {
          // Internet connection restored, trigger sync
          this.checkAndSyncIfNeeded();
        }
      });
    }
    
    this.syncInterval = setInterval(async () => {
      await this.checkAndSyncIfNeeded();
    }, intervalMs);
  }

  // Stop automatic sync
  stopAutoSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
    
    // Clean up app state listener
    if (this.appStateListener) {
      this.appStateListener.remove();
      this.appStateListener = null;
    }

    // Clean up network listener
    if (this.networkListener) {
      this.networkListener();
      this.networkListener = null;
    }
  }

  // Check if there are pending items and sync if needed
  private async checkAndSyncIfNeeded() {
    // Check if there are pending items to sync
    const pendingCount = this.getPendingSyncCount();
    
    if (pendingCount.voters === 0 && pendingCount.addresses === 0 && pendingCount.telling === 0) {
      return;
    }
    
    const netInfo = await NetInfo.fetch();
    if (netInfo.isConnected && netInfo.isInternetReachable) {
      await this.syncPendingData();
    }
  }

  // Manual sync - now works globally across all branches and campaigns
  async syncPendingData(): Promise<SyncResult> {
    if (this.isSyncing) {
      return { success: false, syncedItems: 0, errors: ['Sync already in progress'] };
    }

    // Check network connectivity first before attempting any sync
    const netInfo = await NetInfo.fetch();
    if (!netInfo.isConnected || !netInfo.isInternetReachable) {
      console.log('No internet connection available');  
      return { 
        success: false, 
        syncedItems: 0, 
        errors: ['No internet connection available'] 
      };
    }

    this.isSyncing = true;
    const result: SyncResult = { success: true, syncedItems: 0, errors: [] };
    const updatedEngagements = new Set<string>(); // Track which engagements were updated
    const updatedTellingEngagements = new Set<string>(); // Track telling engagements

    try {
      const store = useCampaignStore.getState();
      const { cachedData } = store;
      
      // Process all branches and campaigns
      for (const [branchId, branchData] of Object.entries(cachedData)) {
        for (const [campaignId, campaignData] of Object.entries(branchData.campaigns || {})) {
          // Process voter updates for this campaign
          for (const [voterId, voterUpdateData] of Object.entries(campaignData.voterUpdates || {})) {
            try {
              // Extract the final state from the voter update data (excluding the internal updates array)
              const { updates, ...voterUpdate } = voterUpdateData;
              
              // Track which engagement this voter update affects
              if (voterUpdate.engagement_id) {
                updatedEngagements.add(`${branchId}:${campaignId}:${voterUpdate.engagement_id}`);
              }
              
              if (! await this.syncIndividualVoter(voterId, voterUpdate, branchId)) continue;
              
              // Clear voter update from store
              const newCachedData = { ...store.cachedData };
              if (newCachedData[branchId]?.campaigns?.[campaignId]?.voterUpdates) {
                delete newCachedData[branchId].campaigns[campaignId].voterUpdates[voterId];
              }
              useCampaignStore.setState({ cachedData: newCachedData });
              result.syncedItems++;
            } catch (error) {
                console.warn(`Failed to sync voter ${voterId} in branch ${branchId}, campaign ${campaignId}:`, error);
                result.errors.push(`Failed to sync voter ${voterId} in branch ${branchId}, campaign ${campaignId}: ${error}`);
                
                // Log error to Sentry for monitoring
                Sentry.captureException(error, {
                  tags: {
                    component: 'syncService',
                    operation: 'voterSync',
                    voterId,
                    branchId,
                    campaignId
                  },
                  extra: {
                    voterUpdateData,
                    errorMessage: error instanceof Error ? error.message : String(error)
                  }
                });
                
                // Keep the update for retry - don't delete it
              }
          }

          // Process address updates for this campaign
          for (const [addressId, addressUpdateData] of Object.entries(campaignData.addressUpdates || {})) {
            try {
              // Extract the final state from the address update data (excluding the internal updates array)
              const { updates, ...addressUpdate } = addressUpdateData;
              
              // Check if addressUpdate has engagement_id directly
              const engagementId = addressUpdate.engagement_id;
              
              if (!engagementId) {
                console.error(`No engagement_id found for address ${addressId} in branch ${branchId}, campaign ${campaignId}`);
                result.errors.push(`No engagement_id found for address ${addressId} in branch ${branchId}, campaign ${campaignId}`);
                continue;
              }
              
              // Track which engagement this address update affects
              updatedEngagements.add(`${branchId}:${campaignId}:${engagementId}`);
              
              if (!await this.syncAddressUpdate({
                addressId,
                updates: addressUpdate,
                engagement_id: engagementId
              }, branchId)) continue;
              
              // Clear address update from store
              const newCachedData = { ...store.cachedData };
              if (newCachedData[branchId]?.campaigns?.[campaignId]?.addressUpdates) {
                delete newCachedData[branchId].campaigns[campaignId].addressUpdates[addressId];
              }
              useCampaignStore.setState({ cachedData: newCachedData });
              result.syncedItems++;
            } catch (error) {
              console.warn(`Failed to sync address ${addressId} in branch ${branchId}, campaign ${campaignId}:`, error);
              result.errors.push(`Failed to sync address ${addressId} in branch ${branchId}, campaign ${campaignId}: ${error}`);
              
              // Log error to Sentry for monitoring
              Sentry.captureException(error, {
                tags: {
                  component: 'syncService',
                  operation: 'addressSync',
                  addressId,
                  branchId,
                  campaignId
                },
                extra: {
                  addressUpdateData,
                  errorMessage: error instanceof Error ? error.message : String(error)
                }
              });
              
              // Keep the update for retry - don't delete it
            }
          }

          // Process telling updates for this campaign
          for (const [voterId, tellingUpdateData] of Object.entries(campaignData.tellingUpdates || {})) {
            try {
              // Extract the final state from the telling update data (excluding the internal updates array)
              const { updates, ...tellingUpdate } = tellingUpdateData;
              
              // Check if tellingUpdate has engagement_id
              const engagementId = tellingUpdate.engagement_id;
              
              if (!engagementId) {
                console.error(`No engagement_id found for telling update ${voterId} in branch ${branchId}, campaign ${campaignId}`);
                result.errors.push(`No engagement_id found for telling update ${voterId} in branch ${branchId}, campaign ${campaignId}`);
                continue;
              }
              
              // Track engagement for telling update refresh
              updatedTellingEngagements.add(`${branchId}:${campaignId}:${engagementId}`);
              
              await this.syncTellingVoterVoted(
                voterId,
                engagementId,
                tellingUpdate.voted_yet || false,
                branchId
              );
              
              // Clear telling update from store
              const newCachedData = { ...store.cachedData };
              if (newCachedData[branchId]?.campaigns?.[campaignId]?.tellingUpdates) {
                delete newCachedData[branchId].campaigns[campaignId].tellingUpdates[voterId];
              }
              useCampaignStore.setState({ cachedData: newCachedData });
              result.syncedItems++;
            } catch (error) {
              console.warn(`Failed to sync telling update ${voterId} in branch ${branchId}, campaign ${campaignId}:`, error);
              result.errors.push(`Failed to sync telling update ${voterId} in branch ${branchId}, campaign ${campaignId}: ${error}`);
              
              // Log error to Sentry for monitoring
              Sentry.captureException(error, {
                tags: {
                  component: 'syncService',
                  operation: 'tellingSync',
                  voterId,
                  branchId,
                  campaignId
                },
                extra: {
                  tellingUpdateData,
                  errorMessage: error instanceof Error ? error.message : String(error)
                }
              });
              
              // Keep the update for retry - don't delete it
            }
          }
        }
      }

      if (result.errors.length > 0) {
        result.success = false;
      }

      // If we successfully synced any items, trigger a refetch of data
      if (result.syncedItems > 0) {
        this.triggerAddressDataRefetch(updatedEngagements);
        // Removed automatic refetch of telling data - local state updates provide immediate UI feedback
        // this.triggerTellingDataRefetch(updatedTellingEngagements);
      }

    } catch (error) {
      result.success = false;
      result.errors.push(`Sync failed: ${error}`);
    } finally {
      this.isSyncing = false;
    }

    return result;
  }

  // Sync individual voter update
  private async syncIndividualVoter(voterId: string, voterUpdate: any, branchId: string): Promise<boolean> {
    
    if (!branchId) {
      throw new Error('No branch context provided');
    }

    // Get address_id and engagement_id from the voter update
    const addressId = voterUpdate.address_id;
    const engagementId = voterUpdate.engagement_id;
    
    if (!addressId) {
      throw new Error(`No address_id found for voter ${voterId}`);
    }
    
    if (!engagementId) {
      throw new Error(`No engagement_id found for voter ${voterId}`);
    }

    // Step 1: Create or get interaction ID for this address
    const interactionId = await this.getOrCreateInteraction(
      engagementId,
      addressId,
      branchId,
      false, // notIn
    );

    if (!interactionId) {
      return false;
    }

    // Step 2: Determine if this is a GOTV update or regular canvassing update
    const gotvFields = ['voted_yet', 'will_vote', 'will_not_vote', 'confirmation_type', 'voting_confirmation_timestamp', 'has_been_contacted', 'gotv_notes', 'will_vote_by_time', 'requested_lift', 'requested_lift_preferred_windows'];
    const hasGotvFields = gotvFields.some(field => voterUpdate.hasOwnProperty(field));
    
    if (hasGotvFields) {
      // This is a GOTV update - use the GOTV API
      await this.syncGotvUpdate(voterId, voterUpdate, interactionId, branchId);
    } else {
      // This is a regular canvassing update - use the canvassing API
      await this.syncCanvassingUpdate(voterId, voterUpdate, interactionId, branchId);
    }

    return true;
  }

  // Sync GOTV update using the new GOTV API
  private async syncGotvUpdate(voterId: string, voterUpdate: any, interactionId: string, branchId: string): Promise<void> {
    // Filter out routing fields that shouldn't be sent to GOTV API
    const { address_id, engagement_id, ...gotvUpdates } = voterUpdate;
    
    const gotvUpdateBody = {
      interaction_id: interactionId,
      update: {
        voter_id: voterId,
        ...gotvUpdates,
      },
    };

    const response = await authenticatedFetch(
      branchId,
      '/gotv/update-gotv-status',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(gotvUpdateBody),
      },
      'groundforce'
    );

    if (!response.ok) {
      let errorText = '';
      try {
        errorText = await response.text();
      } catch (e) {
        errorText = 'Response body already consumed';
      }
      console.error(`Failed to update GOTV status for voter ${voterId}:`, errorText);
      console.error(`Response status: ${response.status}`);
      console.error(`Response headers:`, Object.fromEntries(response.headers.entries()));
      throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
    }
  }

  // Sync regular canvassing update using the canvassing API
  private async syncCanvassingUpdate(voterId: string, voterUpdate: any, interactionId: string, branchId: string): Promise<void> {
    // Filter out routing fields that shouldn't be stored in voter_info table
    const { address_id, engagement_id, ...voterInfoUpdates } = voterUpdate;
    
    const voterUpdateBody: any = {
      interaction_id: interactionId,
      voter_id: voterId,
      voter_info_updates: voterInfoUpdates,
    };

    const response = await authenticatedFetch(
      branchId,
      '/canvassing/update-voter-info',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(voterUpdateBody),
      },
      'groundforce'
    );

    if (!response.ok) {
      let errorText = '';
      try {
        errorText = await response.text();
      } catch (e) {
        errorText = 'Response body already consumed';
      }
      console.error(`Failed to update voter ${voterId}:`, errorText);
      console.error(`Response status: ${response.status}`);
      console.error(`Response headers:`, Object.fromEntries(response.headers.entries()));
      throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
    }
  }

  // Get or create interaction for an address
  private async getOrCreateInteraction(
    engagementId: string,
    addressId: string,
    branchId: string,
    notIn: boolean = false
  ): Promise<string | null> {
    // Check if we already have an interaction ID for this address
    const store = useCampaignStore.getState();
    const cachedData = store.cachedData[branchId];
    
    if (!cachedData) {
      // Create new interaction without checking for existing ones
    } else {
      // Look for any address that has a valid persistent interaction_id
      for (const campaignId in cachedData.campaigns) {
        const campaignData = cachedData.campaigns[campaignId];
        if (!campaignData || !campaignData.interactionIds) {
          continue;
        }
        const interactionData = campaignData.interactionIds[addressId];
        if (interactionData) {
          const oneHourAgo = Date.now() - (60 * 60 * 1000); // 1 hour ago
          // Check if the cached interaction has the same notIn value and is not expired
          if (interactionData.timestamp > oneHourAgo && interactionData.notIn === notIn) {
            return interactionData.interactionId;
          } else {
            // Remove expired or mismatched interaction ID
            const newCachedData = { ...store.cachedData };
            if (newCachedData[branchId] && newCachedData[branchId].campaigns[campaignId] && newCachedData[branchId].campaigns[campaignId].interactionIds) {
              delete newCachedData[branchId].campaigns[campaignId].interactionIds[addressId];    
              useCampaignStore.setState({ cachedData: newCachedData });
            }
          }
        }
      }
    }

    // Create new interaction
    try {
      // Get location if available (non-blocking - don't fail if location unavailable)
      let interactionLocation: [number, number] | null = null;
      try {
        interactionLocation = await locationService.getInteractionLocation();
      } catch (locationError) {
        // Log but don't fail interaction creation if location fails
        console.warn('Failed to get location for interaction:', locationError);
      }

      const requestBody: any = {
        engagement_id: engagementId,
        address_id: addressId,
        not_in: notIn,
        interaction_notes: notIn ? "Household marked as not in" : "Household visit started"
      };

      // Add location if available
      if (interactionLocation) {
        requestBody.interaction_location = interactionLocation;
      }

      const response = await authenticatedFetch(
        branchId,
        '/canvassing/record-interaction',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        },
        'groundforce'
      );

      if (response.ok) {
        const data = await response.json();
        const interactionId = data.interaction_id;
        
        // Store the interaction ID using the store method
        store.storeInteractionId(addressId, interactionId, notIn);
        
        return interactionId;
      } else {
        Sentry.captureException(new Error('Failed to create interaction status code: ' + response.status), {
          tags: {
            component: 'syncService',
            operation: 'interactionSync',
            engagementId,
            addressId,
        }});
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          component: 'syncService',
          operation: 'interactionSync',
          engagementId,
          addressId,
        }});
    }

    return null;
  }

  // Get or create interaction for telling (without address_id)
  private async getOrCreateTellingInteraction(
    engagementId: string,
    voterId: string,
    branchId: string
  ): Promise<string | null> {
    if (!branchId) {
      throw new Error('No branch context provided');
    }

    // Check if we already have a telling interaction for this engagement
    const store = useCampaignStore.getState();
    const { selectedCampaign } = store;
    
    if (!selectedCampaign) {
      throw new Error('No campaign context available');
    }

    const campaignData = store.cachedData[branchId]?.campaigns?.[selectedCampaign];
    
    // Create composite key for searching
    // The correct composite key format is `${engagementId}:${voterId}` to match storeTellingInteractionId and campaignStore structure
    const key = `${engagementId}:${voterId}`;
    
    if (campaignData?.tellingInteractionIds?.[key]) {
      const existingInteraction = campaignData.tellingInteractionIds[key];
      // Check if interaction is still valid (not expired)
      const isExpired = Date.now() - existingInteraction.timestamp > 24 * 60 * 60 * 1000; // 24 hours
      if (!isExpired) {
        return existingInteraction.interactionId;
      }
    }

    // Create new telling interaction
    try {
      const requestBody: any = {
        voter_id: voterId,
        engagement_id: engagementId,
        interaction_notes: "Telling engagement started"
      };

      const response = await authenticatedFetch(
        branchId,
        '/canvassing/record-interaction',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        },
        'groundforce'
      );

      if (response.ok) {
        const data = await response.json();
        const interactionId = data.interaction_id;
        
        // Store the interaction ID using the store method
        store.storeTellingInteractionId(engagementId, interactionId, voterId);
        
        return interactionId;
      } else {
        Sentry.captureException(new Error('Failed to create telling interaction status code: ' + response.status), {
          tags: {
            component: 'syncService',
            operation: 'tellingInteractionSync',
        }});
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          component: 'syncService',
          operation: 'tellingInteractionSync',
        }});
    }

    return null;
  }

  // Trigger a refetch of address data only for engagements that had updates
  private triggerAddressDataRefetch(updatedEngagements: Set<string>) {
    // Only refetch addresses for engagements that actually had updates
    for (const engagementKey of updatedEngagements) {
      const [branchId, campaignId, engagementId] = engagementKey.split(':');
      if (branchId && campaignId && engagementId) {
        this.refetchEngagementAddresses(branchId, campaignId, engagementId);
      }
    }
  }



  // Refetch address data for a specific engagement
  private async refetchEngagementAddresses(branchId: string, campaignId: string, engagementId: string) {
    try {
      const store = useCampaignStore.getState();
      
      // Fetch addresses
      await store.fetchAddressesForEngagement(engagementId, branchId, campaignId);

      // Get the battle plan ID from existing engagement data
      const campaignData = store.cachedData[branchId]?.campaigns?.[campaignId];
      let battlePlanId: string | undefined;
      
      if (campaignData) {
        // Find the engagement in any battle plan to get the battle_plan_id
        for (const [bpId, engagements] of Object.entries(campaignData.engagements || {})) {
          const engagement = engagements.find(eng => eng.engagement_id === engagementId);
          if (engagement) {
            battlePlanId = bpId;
            break;
          }
        }
      }
      
      // Also refresh engagement info to get updated counts
      if (battlePlanId) {
        try {
          const updatedEngagement = await store.fetchEngagementInfo(engagementId, battlePlanId, branchId, campaignId);
          
          // Also update my active engagements if this engagement is in the list
          if (updatedEngagement) {
            const myActiveEngagements = store.getMyActiveEngagements();
            const isInMyEngagements = myActiveEngagements.some(eng => eng.engagement_id === engagementId);
            
            if (isInMyEngagements) {
              // Update the engagement in my active engagements list
              const updatedMyEngagements = myActiveEngagements.map(eng => 
                eng.engagement_id === engagementId ? updatedEngagement : eng
              );
              
              // Update the store with the new my active engagements list
              const newCachedData = { ...store.cachedData };
              if (newCachedData[branchId]) {
                newCachedData[branchId].myActiveEngagements = updatedMyEngagements;
                useCampaignStore.setState({ cachedData: newCachedData });
              }
            }
          }
        } catch (error) {
          console.warn(`Failed to refresh engagement info for engagement ${engagementId}:`, error);
          // Don't fail the address refetch if engagement info refresh fails
        }
      }
    } catch (error) {
      // Silently fail - this is a background refresh
      console.warn(`Failed to refetch addresses for engagement ${engagementId}:`, error);
    }
  }


  // Sync individual address update
  private async syncAddressUpdate(addressUpdate: any, branchId: string): Promise<boolean> {
    if (!branchId) {
      throw new Error('No branch context provided');
    }

    // Extract the actual update data and engagement_id
    const updates = addressUpdate.updates || addressUpdate;
    const engagementId = addressUpdate.engagement_id;
    const addressId = addressUpdate.addressId;
    
    // Create interaction with not_in set to true
    const interactionId = await this.getOrCreateInteraction(
        engagementId,
        addressId,
        branchId,
        updates.not_in
    );

    if (!interactionId) {
      return false;
    } else {
      return true;
    }
  }

  // Check if there are pending sync items across all branches and campaigns
  hasPendingSync(): boolean {
    const store = useCampaignStore.getState();
    const { cachedData } = store;
    
    for (const branchData of Object.values(cachedData)) {
      for (const campaignData of Object.values(branchData.campaigns || {})) {
        if (Object.keys(campaignData.voterUpdates || {}).length > 0 || 
            Object.keys(campaignData.addressUpdates || {}).length > 0 ||
            Object.keys(campaignData.tellingUpdates || {}).length > 0) {
          return true;
        }
      }
    }
    
    return false;
  }

  // Get pending sync count across all branches and campaigns
  getPendingSyncCount(): { voters: number; addresses: number; telling: number } {
    const store = useCampaignStore.getState();
    const { cachedData } = store;
    
    let totalVoters = 0;
    let totalAddresses = 0;
    let totalTelling = 0;
    
    for (const branchData of Object.values(cachedData)) {
      for (const campaignData of Object.values(branchData.campaigns || {})) {
        totalVoters += Object.keys(campaignData.voterUpdates || {}).length;
        totalAddresses += Object.keys(campaignData.addressUpdates || {}).length;
        totalTelling += Object.keys(campaignData.tellingUpdates || {}).length;
      }
    }
    
    return {
      voters: totalVoters,
      addresses: totalAddresses,
      telling: totalTelling,
    };
  }

  // Sync telling voter voted status
  async syncTellingVoterVoted(
    voterId: string,
    engagementId: string,
    votedYet: boolean,
    branchId: string
  ): Promise<void> {
    if (!branchId) {
      throw new Error('No branch context provided');
    }

    // Step 1: Create or get interaction ID for this telling engagement
    const interactionId = await this.getOrCreateTellingInteraction(engagementId, voterId, branchId);

    if (!interactionId) {
      throw new Error('Failed to create telling interaction');
    }

    // Step 2: Call GOTV API with the interaction ID
    const gotvUpdateBody = {
      interaction_id: interactionId,
      update: {
        voter_id: voterId,
        voted_yet: votedYet,
      },
    };

    const response = await authenticatedFetch(
      branchId,
      '/gotv/update-gotv-status',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(gotvUpdateBody),
      },
      'groundforce'
    );

    if (!response.ok) {
      let errorText = '';
      try {
        errorText = await response.text();
      } catch (e) {
        errorText = 'Response body already consumed';
      }
      console.error(`Failed to update GOTV status for telling voter ${voterId}:`, errorText);
      console.error(`Response status: ${response.status}`);
      console.error(`Response headers:`, Object.fromEntries(response.headers.entries()));
      throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
    }
  }

  // Manual cleanup trigger for testing/debugging
  // Uses existing clearEngagementData for each inactive engagement
  async cleanupInactiveEngagements(): Promise<void> {
    try {
      const store = useCampaignStore.getState();
      const { cachedData } = store;

      // Process all branches
      for (const [branchId, branchData] of Object.entries(cachedData)) {
        // Get user's active engagements for this branch
        const activeEngagements = branchData.myActiveEngagements || [];
        const activeEngagementIds = new Set(activeEngagements.map(e => e.engagement_id));

        // Process all campaigns in this branch
        for (const [campaignId, campaignData] of Object.entries(branchData.campaigns || {})) {
          // Collect all engagement IDs that have cached data
          const cachedEngagementIds = new Set<string>();
          
          // Check all data stores for engagement IDs
          Object.keys(campaignData.addresses || {}).forEach(id => cachedEngagementIds.add(id));
          Object.keys(campaignData.postcodeSlicePolygons || {}).forEach(id => cachedEngagementIds.add(id));
          
          // Clean up engagement-based telling voters data for inactive engagements
          const cachedEngagementKeys = Object.keys(campaignData.tellingVotersByEngagements || {});
          
          let engagementDataCleaned = false;
          for (const engagementIdsKey of cachedEngagementKeys) {
            // Extract engagement IDs from composite key (format: "engagementId1,engagementId2,engagementId3")
            const engagementIds = engagementIdsKey.split(',');
            
            // Check if any of the engagements in this combination are still active
            const hasActiveEngagement = engagementIds.some(id => activeEngagementIds.has(id));
            
            if (!hasActiveEngagement) {
              // None of the engagements in this combination are active, clean it up
              delete campaignData.tellingVotersByEngagements[engagementIdsKey];
              engagementDataCleaned = true;
            }
          }
          
          // Update the store if we cleaned up any engagement data
          if (engagementDataCleaned) {
            useCampaignStore.setState({ cachedData });
          }

          // Check each cached engagement
          for (const engagementId of cachedEngagementIds) {
            // Skip if this is an active engagement
            if (activeEngagementIds.has(engagementId)) {
              continue;
            }

            // Set context temporarily to ensure clearEngagementData works correctly
            const previousBranch = store.selectedBranch;
            const previousCampaign = store.selectedCampaign;
            
            useCampaignStore.setState({
              selectedBranch: branchId,
              selectedCampaign: campaignId
            });
            
            // Use existing clearEngagementData function
            store.clearEngagementData(engagementId);
            
            // Restore previous context
            useCampaignStore.setState({
              selectedBranch: previousBranch,
              selectedCampaign: previousCampaign
            });
          }
        }
      }
    } catch (error) {
      console.error('[Cleanup] Error during inactive engagement cleanup:', error);
      throw error;
    }
  }

}

// Export singleton instance
export const syncService = new SyncService();

// Hook for components to use sync service
export const useSyncService = () => {
  return {
    syncPendingData: syncService.syncPendingData.bind(syncService),
    hasPendingSync: syncService.hasPendingSync.bind(syncService),
    getPendingSyncCount: syncService.getPendingSyncCount.bind(syncService),
    startAutoSync: syncService.startAutoSync.bind(syncService),
    stopAutoSync: syncService.stopAutoSync.bind(syncService),
    syncTellingVoterVoted: syncService.syncTellingVoterVoted.bind(syncService),
    cleanupInactiveEngagements: syncService.cleanupInactiveEngagements.bind(syncService),
  };
};
